import {
  getEventActionName,
  getSuccessActionName,
} from "../../core/make-action";
import ViewerEvent from "../../core/ViewerEvent";
import { combineReducers } from "redux";
import ViewerMethod from "../../core/ViewerMethod";
import { persistReducer } from "../../core/persistReducer";
import { clamp } from "../../../../../../common/utils/number";
import { isAndroid } from "../../../../../../common/utils/platform";

const persistKey = "tts-controls";

const STATE_VERSION = 1;
// v1 rate changed from percent to raw rate

function playbackStatus(state = { playing: false, paused: false }, action) {
  switch (action.type) {
    case getEventActionName(ViewerEvent.TTS_STATUS):
      const { playing, paused } = action.eventData;
      return { playing, paused };
    default:
      return state;
  }
}

function decentered(state = false, action) {
  switch (action.type) {
    case getEventActionName(ViewerEvent.TTS_DECENTERED):
      return action.eventData.decentered;
    default:
      return state;
  }
}

function rate(state = 1, action) {
  switch (action.type) {
    case getSuccessActionName(ViewerMethod.TTS_GET_SETTINGS):
    case getSuccessActionName(ViewerMethod.TTS_SET_RAW_RATE):
      return action.resultData.rate;
    default:
      return state;
  }
}

function voiceIndex(state = 0, action) {
  switch (action.type) {
    case getSuccessActionName(ViewerMethod.TTS_GET_SETTINGS):
    case getSuccessActionName(ViewerMethod.TTS_SET_VOICE_INDEX):
      return action.resultData.voiceIndex;
    default:
      return state;
  }
}

function voices(state = [], action) {
  switch (action.type) {
    case getSuccessActionName(ViewerMethod.TTS_GET_VOICES):
      return action.resultData.voices;
    default:
      return state;
  }
}

function altEnabled(state = false, action) {
  switch (action.type) {
    case getSuccessActionName(ViewerMethod.TTS_ENABLE_ALT_TEXT):
    case getSuccessActionName(ViewerMethod.TTS_DISABLE_ALT_TEXT):
      return action.resultData.altEnabled;
    default:
      return state;
  }
}

function voiceName(state = "", action) {
  // Adding voice name to reducer. At first glance this seems pointless without case statements but its used to set up initial state and is changed
  // from the local storage logic below.
  switch (action.type) {
    default:
      return state;
  }
}

function voiceLang(state = "", action) {
  // cf. voiceName above. storing voice lang gives a better chance of re-finding voice cross browser
  switch (action.type) {
    default:
      return state;
  }
}

const filteredCombineReducers = (reducers) => {
  // tts state is a composite of user and book persisted state
  // but we need to feed the reducers only relevant state
  const combinedReducers = combineReducers(reducers);
  const reducerKeys = Object.keys(reducers);
  return function (state, action) {
    const filteredState = state
      ? Object.keys(state)
          .filter((key) => reducerKeys.includes(key))
          .reduce((obj, key) => {
            obj[key] = state[key];
            return obj;
          }, {})
      : state;
    return combinedReducers(filteredState, action as never);
  };
};

const userPersistReducer = persistReducer(
  persistKey,
  filteredCombineReducers({
    playbackStatus,
    decentered,
    voices,
    voiceIndex,
    altEnabled,
    rate,
    voiceName,
    voiceLang,
  }),
  {
    stateToStorage: (state) => {
      return {
        altEnabled: state.altEnabled,
        rate: state.rate,
        voiceIndex: state.voiceIndex,
        voiceName: state.voices[state.voiceIndex]?.name,
        // android is bad at voices, it will hinder not help reacquisition
        voiceLang: isAndroid() ? null : state.voices[state.voiceIndex]?.lang,
      };
    },
    storageToState: (store) => {
      return {
        altEnabled: store.altEnabled,
        rate: store.rate,
        voiceIndex: store.voiceIndex,
        voiceName: store.voiceName,
        voiceLang: store.voiceLang,
        voices: [],
        playbackStatus: { playing: false, paused: false },
      };
    },
    byBook: false,
    version: STATE_VERSION,
    migrate: (value, from, to) => {
      if (typeof value?.rate === "number") {
        if (from === 0 && to === 1) {
          // this maps a 0-1 slider percentage to a 0.1 -> 6 actual
          // rate calculation originally in mosaic adapter.js
          const oldRate =
            0.1 -
            (-0.6775545 / -3.429597) * (1 - Math.exp(3.429597 * value.rate));
          // going forward we use the actual rate in a range 0.5 -> 4
          value.rate = clamp(oldRate, 0.5, 4);
          return value;
        }
      }
      // returning null will fall back to default state
      return null;
    },
  }
);

function simplifiedReadingEnabled(state = false, action) {
  switch (action.type) {
    case getSuccessActionName(ViewerMethod.TTS_ENABLE_SIMPLIFIED_READING):
    case getSuccessActionName(ViewerMethod.TTS_DISABLE_SIMPLIFIED_READING):
      return action.resultData.simplifiedReadingEnabled;
    default:
      return state;
  }
}

const bookPersistReducer = persistReducer(
  persistKey,
  filteredCombineReducers({ simplifiedReadingEnabled }),
  {
    // note normally state/storage noops can be omitted for default treatment
    // but because state is being split, these are needed to pick out a subset
    stateToStorage: (state) => {
      return {
        simplifiedReadingEnabled: state.simplifiedReadingEnabled,
      };
    },
    storageToState: (store) => {
      return {
        simplifiedReadingEnabled: store.simplifiedReadingEnabled,
      };
    },
    byBook: true,
    version: STATE_VERSION,
  }
);

// special case because some tts state is persisted to book and some to
// user but the state is conceptually "together"
export default (state, action) => ({
  ...userPersistReducer(state, action),
  ...bookPersistReducer(state, action),
});
